// Type-only import: this module is pulled into GalleryView.hook, whose tests mock
// services/api with a bare factory — a runtime import from there would be undefined.
import type { OrderType, ResourcesOrdering, TimelineOrderBy } from '../../services/api'

export interface GallerySort {
  orderBy: ResourcesOrdering
  order: OrderType
}

export const DEFAULT_SORT: GallerySort = { orderBy: 'date_taken', order: 'desc' }

const PARAM_KEYS = {
  orderBy: 'order_by',
  order: 'order',
} as const

/** Orderings whose sort instant equals the item `date` and the timeline bucket key. */
export const isTimelineOrderBy = (value: string | undefined): value is TimelineOrderBy =>
  value === 'date_taken' || value === 'date_uploaded'

export const isDefaultSort = (sort: GallerySort): boolean =>
  sort.orderBy === DEFAULT_SORT.orderBy && sort.order === DEFAULT_SORT.order

interface SortContext {
  /** date_added_to_album only sorts server-side when an album filter is set */
  hasAlbum: boolean
}

const orderByOrUndefined = (
  value: string | null,
  { hasAlbum }: SortContext
): ResourcesOrdering | undefined => {
  if (value === 'date_taken' || value === 'date_uploaded') return value
  if (value === 'date_added_to_album' && hasAlbum) return value
  return undefined
}

const orderOrUndefined = (value: string | null): OrderType | undefined =>
  value === 'asc' || value === 'desc' ? value : undefined

/**
 * Unsupported values fall back to the default rather than being rewritten out of the URL —
 * a shared link carrying date_added_to_album stays intact for the album screen.
 */
export const parseGallerySort = (search: string, context: SortContext): GallerySort => {
  const params = new URLSearchParams(search)

  return {
    orderBy: orderByOrUndefined(params.get(PARAM_KEYS.orderBy), context) ?? DEFAULT_SORT.orderBy,
    order: orderOrUndefined(params.get(PARAM_KEYS.order)) ?? DEFAULT_SORT.order,
  }
}

/** Rewrites only the sort params; unrelated params in baseSearch are preserved. */
export const serializeGallerySort = (sort: GallerySort, baseSearch = ''): string => {
  const params = new URLSearchParams(baseSearch)

  for (const key of Object.values(PARAM_KEYS)) params.delete(key)

  // defaults stay out of the URL so a plain gallery link has no sort noise
  if (sort.orderBy !== DEFAULT_SORT.orderBy) params.set(PARAM_KEYS.orderBy, sort.orderBy)
  if (sort.order !== DEFAULT_SORT.order) params.set(PARAM_KEYS.order, sort.order)

  return params.toString()
}
